/**
 * Dedicated OHT corridor endpoints (From / To).
 *
 * Stored values (semantic):
 *   entry | exit | {TYPE}:RAW | {TYPE}:FINISHED | {TYPE}:TOOLS
 *
 * Dropdown labels come from port Name + Display Name on the layout when available.
 */

import type { LayoutElement } from '../../types';
import type { EquipmentInstance, EquipmentTemplate } from '../equipment/equipmentTypes';

function resolveProcessType(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

export type AmhsOhtRole = 'entry' | 'exit' | 'group' | 'interbay' | 'outerloop';

/** UI Role dropdown for dedicated OHTs. */
export const OHT_ROLE_OPTIONS: ReadonlyArray<{ value: AmhsOhtRole; label: string }> = [
  { value: 'entry', label: 'Entry' },
  { value: 'group', label: 'Process bay' },
  { value: 'outerloop', label: 'Outer loop' },
  { value: 'interbay', label: 'Inter bay' },
  { value: 'exit', label: 'Exit' },
];

export type DedicatedEndpointOption = { value: string; label: string };

export type AmhsDedicatedPathStep = Record<string, string>;

export function parseOhtRole(raw: unknown): AmhsOhtRole | undefined {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (
    s === 'entry' ||
    s === 'exit' ||
    s === 'group' ||
    s === 'interbay' ||
    s === 'outerloop'
  ) {
    return s;
  }
  if (s === 'process_bay') return 'group';
  if (s === 'inter_bay') return 'interbay';
  if (s === 'outer_loop') return 'outerloop';
  return undefined;
}

export function labelOhtRole(role: string | null | undefined): string {
  const parsed = parseOhtRole(role);
  if (!parsed) return String(role ?? '').trim() || '—';
  return OHT_ROLE_OPTIONS.find((o) => o.value === parsed)?.label ?? parsed;
}

/**
 * Build `dedicatedPath` for the simulation payload from role + process type(s).
 *
 * | Role        | Path                                      |
 * |-------------|-------------------------------------------|
 * | entry       | ENTRY → STOCKER(type)                     |
 * | group       | STOCKER → TOOL → STOCKER (same type)      |
 * | interbay    | STOCKER(A) → STOCKER(B)                   |
 * | exit        | STOCKER(type) → EXIT                      |
 */
export function buildDedicatedPath(
  role: AmhsOhtRole,
  toolType?: string | null,
  toToolType?: string | null
): AmhsDedicatedPathStep[] | undefined {
  const type = String(toolType ?? '')
    .trim()
    .toUpperCase();
  const toType = String(toToolType ?? '')
    .trim()
    .toUpperCase();
  if (role === 'entry' && type) {
    return [{ ENTRY: 'ENTRY' }, { STOCKER: type }];
  }
  if (role === 'group' && type) {
    return [{ STOCKER: type }, { TOOL: type }, { STOCKER: type }];
  }
  if (role === 'interbay' && type && toType) {
    return [{ STOCKER: type }, { STOCKER: toType }];
  }
  if (role === 'exit' && type) {
    return [{ STOCKER: type }, { EXIT: 'EXIT' }];
  }
  return undefined;
}

/** Option value may be `SEMANTIC` or `SEMANTIC@@portId` for unique tool-port rows. */
export function semanticDedicatedEndpoint(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const idx = s.indexOf('@@');
  return idx >= 0 ? s.slice(0, idx) : s;
}

const GATE_ENDPOINTS: DedicatedEndpointOption[] = [
  { value: 'entry', label: 'Entry' },
  { value: 'exit', label: 'Exit' },
];

export function encodeStockerRaw(toolType: string): string {
  return `${toolType.trim().toUpperCase()}:RAW`;
}

export function encodeStockerFinished(toolType: string): string {
  return `${toolType.trim().toUpperCase()}:FINISHED`;
}

export function encodeTools(toolType: string): string {
  return `${toolType.trim().toUpperCase()}:TOOLS`;
}

export function parseDedicatedEndpoint(raw: string | null | undefined): {
  kind: 'entry' | 'exit' | 'stocker_raw' | 'stocker_finished' | 'tools';
  toolType?: string;
} | null {
  const s = semanticDedicatedEndpoint(raw);
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === 'entry') return { kind: 'entry' };
  if (lower === 'exit') return { kind: 'exit' };

  const m = /^([A-Za-z0-9_-]+):(RAW|FINISHED|TOOLS)$/i.exec(s);
  if (!m) return null;
  const toolType = m[1].toUpperCase();
  const port = m[2].toUpperCase();
  if (port === 'RAW') return { kind: 'stocker_raw', toolType };
  if (port === 'FINISHED') return { kind: 'stocker_finished', toolType };
  return { kind: 'tools', toolType };
}

export function labelDedicatedEndpoint(
  value: string,
  options?: DedicatedEndpointOption[]
): string {
  const exact = options?.find((o) => o.value === value);
  if (exact) return exact.label;
  const semantic = semanticDedicatedEndpoint(value);
  const bySemantic = options?.find((o) => semanticDedicatedEndpoint(o.value) === semantic);
  if (bySemantic) return bySemantic.label;

  const parsed = parseDedicatedEndpoint(value);
  if (!parsed) return value || '—';
  if (parsed.kind === 'entry') return 'Entry';
  if (parsed.kind === 'exit') return 'Exit';
  const t = parsed.toolType ?? '';
  if (parsed.kind === 'stocker_raw') return `${t} Stocker Raw`;
  if (parsed.kind === 'stocker_finished') return `${t} Stocker Finished`;
  return `${t} Tools`;
}

/** @deprecated Prefer buildDedicatedEndpointOptionsFromLayout — kept for tests/fallback. */
export function buildDedicatedEndpointOptions(toolTypes: string[]): DedicatedEndpointOption[] {
  const types = [...new Set(toolTypes.map((t) => t.trim().toUpperCase()).filter(Boolean))].sort();
  const options: DedicatedEndpointOption[] = [...GATE_ENDPOINTS];
  for (const t of types) {
    options.push(
      { value: encodeStockerRaw(t), label: `${t} Stocker Raw` },
      { value: encodeStockerFinished(t), label: `${t} Stocker Finished` },
      { value: encodeTools(t), label: `${t} Tools` }
    );
  }
  return options;
}

export type BuildEndpointOptionsInput = {
  elements: Record<string, LayoutElement>;
  equipmentInstances?: Record<string, EquipmentInstance>;
  equipmentTemplates?: Record<string, EquipmentTemplate>;
  entryLabel?: string;
  exitLabel?: string;
};

/**
 * Build From/To options:
 *   Entry / Exit
 *   + each stocker by name only (layout + equipment; no Raw/Finished port rows)
 *   + one Tools option per equipment type
 *
 * Semantic values: TYPE:RAW / TYPE:TOOLS for hop matching.
 * Unique UI rows may use `SEMANTIC@@key`; saved corridor values strip to SEMANTIC.
 */
export function buildDedicatedEndpointOptionsFromLayout(
  input: BuildEndpointOptionsInput
): DedicatedEndpointOption[] {
  const { elements, equipmentInstances = {} } = input;
  const options: DedicatedEndpointOption[] = [
    { value: 'entry', label: String(input.entryLabel ?? '').trim() || 'Entry' },
    { value: 'exit', label: String(input.exitLabel ?? '').trim() || 'Exit' },
  ];
  const stockerNameOptions: DedicatedEndpointOption[] = [];
  const toolTypes = new Set<string>();
  const seenStockerIds = new Set<string>();

  const addStocker = (id: string, name: string, processType: string) => {
    if (seenStockerIds.has(id)) return;
    seenStockerIds.add(id);

    const type = resolveProcessType(processType);
    const stockerLabel = String(name ?? '').trim() || (type ? `${type} Stocker` : 'Stocker');
    // Prefer TYPE:RAW when process type is known; otherwise keep a stable stocker key.
    const value = type
      ? `${encodeStockerRaw(type)}@@stocker:${id}`
      : `stocker:${id}`;
    stockerNameOptions.push({ value, label: stockerLabel });
  };

  for (const el of Object.values(elements)) {
    if (el.type !== 'stocker' || !el.metadata.visible) continue;
    const processType =
      el.customProperties?.processType ?? el.customProperties?.toolType;
    addStocker(el.id, el.metadata.name, String(processType ?? ''));
  }

  for (const inst of Object.values(equipmentInstances)) {
    if (inst.objectType === 'stocker') {
      addStocker(inst.id, inst.name, String(inst.equipmentType ?? ''));
      continue;
    }
    const toolType = resolveProcessType(inst.equipmentType);
    if (toolType) toolTypes.add(toolType);
  }

  for (const opt of stockerNameOptions.sort((a, b) => a.label.localeCompare(b.label))) {
    options.push(opt);
  }

  // One bay option per process type (no per-port duplicates).
  for (const t of [...toolTypes].sort()) {
    options.push({ value: encodeTools(t), label: `${t} Tools` });
  }

  return options;
}

/** Map a saved semantic value to a concrete option value (handles @@portId rows). */
export function matchDedicatedOptionValue(
  saved: string,
  options: DedicatedEndpointOption[]
): string {
  const raw = String(saved ?? '').trim();
  if (!raw) return options[0]?.value ?? '';
  if (options.some((o) => o.value === raw)) return raw;
  const semantic = semanticDedicatedEndpoint(raw);
  const match = options.find((o) => semanticDedicatedEndpoint(o.value) === semantic);
  return match?.value ?? raw;
}

export function deriveOhtRoleFromEndpoints(
  fromRaw: string | null | undefined,
  toRaw: string | null | undefined
): AmhsOhtRole | undefined {
  const from = parseDedicatedEndpoint(fromRaw);
  const to = parseDedicatedEndpoint(toRaw);
  if (!from || !to) return undefined;
  if (from.kind === 'entry') return 'entry';
  if (to.kind === 'exit') return 'exit';
  if (
    (from.kind === 'stocker_finished' || from.kind === 'stocker_raw') &&
    (to.kind === 'stocker_raw' || to.kind === 'stocker_finished')
  ) {
    return 'interbay';
  }
  return 'group';
}

/** Prefer the process family this OHT serves (bay type, or destination stocker type). */
export function deriveToolTypeFromEndpoints(
  fromRaw: string | null | undefined,
  toRaw: string | null | undefined
): string | undefined {
  const from = parseDedicatedEndpoint(fromRaw);
  const to = parseDedicatedEndpoint(toRaw);
  if (!from || !to) return undefined;
  if (to.kind === 'tools' || to.kind === 'stocker_raw' || to.kind === 'stocker_finished') {
    return to.toolType;
  }
  if (from.kind === 'tools' || from.kind === 'stocker_raw' || from.kind === 'stocker_finished') {
    return from.toolType;
  }
  return undefined;
}

/**
 * Route-type filter implied by a From→To corridor.
 *
 * Split bay pairs:
 *   TYPE:RAW → TYPE:TOOLS     — inbound / bay / same-stocker handoffs
 *   TYPE:TOOLS → TYPE:FINISHED — return drop only (tool_to_stocker)
 * Single-bay layouts (only RAW→TOOLS) still include tool_to_stocker for back-compat;
 * when both OHTs exist, backend specificity prefers TOOLS→FINISHED for returns.
 */
export function deriveAllowedRouteTypes(
  fromRaw: string | null | undefined,
  toRaw: string | null | undefined
): string[] | undefined {
  const from = parseDedicatedEndpoint(fromRaw);
  const to = parseDedicatedEndpoint(toRaw);
  if (!from || !to) return undefined;

  if (from.kind === 'entry') {
    return ['outside_to_stocker', 'entry_to_tool'];
  }
  if (to.kind === 'exit') {
    return ['stocker_to_exit', 'tool_to_exit'];
  }
  // Explicit return corridor: tools → finished (or tools → raw) stocker port.
  if (
    from.kind === 'tools' &&
    (to.kind === 'stocker_finished' || to.kind === 'stocker_raw')
  ) {
    return ['tool_to_stocker'];
  }
  // Inbound bay corridor: stocker → tools (+ tool_to_tool / same-stocker).
  if (to.kind === 'tools') {
    return [
      'stocker_raw_to_tool',
      'stocker_to_tool',
      'tool_to_tool',
      // Back-compat when no TOOLS→FINISHED OHT exists for this type.
      'tool_to_stocker',
      'stocker_to_stocker',
    ];
  }
  if (from.kind === 'tools') {
    return ['tool_to_stocker'];
  }
  if (
    (from.kind === 'stocker_finished' || from.kind === 'stocker_raw') &&
    (to.kind === 'stocker_raw' || to.kind === 'stocker_finished')
  ) {
    return ['stocker_to_stocker'];
  }
  return undefined;
}

/** Route types shared (fallback) OHTs can take when no dedicated match exists. */
export const DEFAULT_SHARED_OHT_ROUTE_TYPES = [
  'outside_to_stocker',
  'entry_to_tool',
  'stocker_raw_to_tool',
  'stocker_to_tool',
  'tool_to_tool',
  'tool_to_stocker',
  'stocker_to_stocker',
  'stocker_to_exit',
  'tool_to_exit',
] as const;

/** Always include entry/exit outside hops on shared OHTs. */
const SHARED_OHT_REQUIRED_ROUTE_TYPES = [
  'outside_to_stocker',
  'stocker_to_exit',
] as const;

/**
 * Resolve `allowedRouteTypes` for a shared OHT.
 * Uses any saved list (or the shared default), then ensures outside↔stocker hops.
 */
export function resolveSharedOhtRouteTypes(
  existing?: readonly string[] | null
): string[] {
  const base =
    existing && existing.length > 0
      ? existing.map((r) => String(r).trim().toLowerCase()).filter(Boolean)
      : [...DEFAULT_SHARED_OHT_ROUTE_TYPES];
  return [...new Set([...base, ...SHARED_OHT_REQUIRED_ROUTE_TYPES])];
}

export function collectLayoutProcessTypes(
  elements: Record<string, { type?: string; customProperties?: Record<string, unknown> }>,
  equipmentInstances?: Record<string, { equipmentType?: string; objectType?: string }>
): string[] {
  const types = new Set<string>();
  for (const el of Object.values(elements)) {
    if (el.type === 'stocker') {
      const t = resolveProcessType(
        el.customProperties?.processType ?? el.customProperties?.toolType
      );
      if (t) types.add(t);
    }
  }
  if (equipmentInstances) {
    for (const inst of Object.values(equipmentInstances)) {
      const t = resolveProcessType(inst.equipmentType);
      if (t) types.add(t);
    }
  }
  return [...types];
}
